import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, UserPlus, KeyRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const v = (id) => document.getElementById(id)?.value?.trim() || "";
    try {
      if (mode === "login") {
        const user = await login(v("a-email"), v("a-password"));
        toast.success(`Bienvenue ${user.first_name || ""} !`);
        navigate(user.role === "customer" ? "/compte" : "/admin");
      } else if (mode === "register") {
        await register({ email: v("a-email"), password: v("a-password"), first_name: v("a-prenom"), last_name: v("a-nom") });
        toast.success("Votre compte est créé — bienvenue !");
        navigate("/compte");
      } else {
        await api.post("/auth/forgot-password", { email: v("a-email") });
        toast.success("Si un compte existe, un lien temporaire sécurisé sera envoyé.");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-ae max-w-md py-16 md:py-24" data-testid="auth-page">
      <div className="text-center">
        <h1 className="text-3xl sm:text-4xl text-deepgreen" data-testid="auth-title">
          {mode === "login" ? "Me connecter" : mode === "register" ? "Créer mon compte" : "Mot de passe oublié"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Votre compte vous permet de suivre vos événements, réservations, paiements et documents.
        </p>
      </div>

      <div className="card-ae mt-8 p-6 md:p-8">
        {mode !== "forgot" && (
          <div className="mb-6 grid grid-cols-2 gap-2 rounded-full bg-cream p-1" data-testid="auth-tabs">
            <button
              type="button"
              onClick={() => setMode("login")}
              data-testid="auth-tab-login"
              className={`rounded-full py-2 text-xs font-bold uppercase tracking-wider transition-colors ${mode === "login" ? "bg-deepgreen text-white" : "text-muted-foreground"}`}
            >
              Connexion
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              data-testid="auth-tab-register"
              className={`rounded-full py-2 text-xs font-bold uppercase tracking-wider transition-colors ${mode === "register" ? "bg-deepgreen text-white" : "text-muted-foreground"}`}
            >
              Créer un compte
            </button>
          </div>
        )}

        <form className="space-y-4" onSubmit={submit}>
          {error && <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="auth-error">{error}</p>}
          {mode === "register" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="a-prenom">Prénom</label><input id="a-prenom" className="input-ae" data-testid="auth-firstname" /></div>
              <div><label className="label-ae" htmlFor="a-nom">Nom</label><input id="a-nom" className="input-ae" data-testid="auth-lastname" /></div>
            </div>
          )}
          <div><label className="label-ae" htmlFor="a-email">E-mail</label><input id="a-email" type="email" className="input-ae" data-testid="auth-email" /></div>
          {mode !== "forgot" && (
            <div><label className="label-ae" htmlFor="a-password">Mot de passe</label><input id="a-password" type="password" className="input-ae" data-testid="auth-password" /></div>
          )}
          <button type="submit" disabled={loading} className="btn-primary-ae w-full disabled:opacity-50" data-testid="auth-submit">
            {mode === "login" && <><LogIn className="h-4 w-4" /> Me connecter</>}
            {mode === "register" && <><UserPlus className="h-4 w-4" /> Créer mon compte</>}
            {mode === "forgot" && <><KeyRound className="h-4 w-4" /> Recevoir un lien de réinitialisation</>}
          </button>
        </form>

        <div className="mt-5 text-center text-sm">
          {mode === "forgot" ? (
            <button type="button" onClick={() => setMode("login")} className="font-semibold text-deepgreen hover:text-sage" data-testid="auth-back-login">
              Retour à la connexion
            </button>
          ) : (
            <button type="button" onClick={() => setMode("forgot")} className="font-semibold text-deepgreen hover:text-sage" data-testid="auth-forgot-link">
              Mot de passe oublié ?
            </button>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Un lien temporaire sécurisé sera envoyé — jamais votre ancien mot de passe.
        </p>
      </div>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Pas encore de compte ? Vous pouvez aussi <Link to="/boutique" className="font-semibold text-deepgreen" data-testid="auth-continue-browsing">parcourir la boutique librement</Link>.
      </p>
    </div>
  );
}
